
import _ from 'lodash';
import axios from 'axios';
import { adminClient as apolloClient } from '@/store/apollo';

import AuthApi from '@/store/api/system/auth';
import { ensureFreshToken } from '@/helpers';

const PREVIEW_ENDPOINT = `${process.env.VUE_APP_API_ENDPOINT}${process.env.VUE_APP_API_PREVIEW_ROUTE}`;

// import FormEntriesGql from '@/extensions/forms/store/graphql/queries/FormEntries.gql';
// import FormEntryGql from '@/extensions/forms/store/graphql/queries/FormEntry.gql';
import FormEntriesPaginatedGql from '@/extensions/forms/store/graphql/queries/FormEntriesPaginated.gql';
import PreviewSampleFormEntriesGql from '@/extensions/forms/store/graphql/queries/PreviewSampleFormEntries.gql';
// import CreateFormEntryGql from '@/extensions/forms/store/graphql/mutations/CreateFormEntry.gql';
// import UpdateFormEntryGql from '@/extensions/forms/store/graphql/mutations/UpdateFormEntry.gql';
// import DeleteFormEntryGql from '@/extensions/forms/store/graphql/mutations/DeleteFormEntry.gql';
import ProcessFormEntriesPaginationBulkActionGql from '@/extensions/forms/store/graphql/mutations/ProcessFormEntriesPaginationBulkAction.gql';
import UnreadFormEntriesCountGql from '@/extensions/forms/store/graphql/queries/UnreadFormEntriesCount.gql';
import MarkFormEntryReadGql from '@/extensions/forms/store/graphql/mutations/MarkFormEntryRead.gql';

export default {
    // Submissions nobody has opened yet — the menu badge.
    fetchUnreadCount: (cmsId = null) =>
        new Promise((resolve, reject) => {
            apolloClient
                .query({
                    query: UnreadFormEntriesCountGql,
                    variables: { cms_id: cmsId ? parseInt(cmsId) : undefined },
                    fetchPolicy: 'network-only',
                })
                .then(({ data }) => resolve(data.EX_FRMS_UnreadFormEntriesCount))
                .catch((err) => reject(err));
        }),

    markRead: (id, read = true) =>
        new Promise((resolve, reject) => {
            apolloClient
                .mutate({ mutation: MarkFormEntryReadGql, variables: { id: parseInt(id), read } })
                .then(({ data }) => resolve(data.EX_FRMS_MarkFormEntryRead))
                .catch((err) => reject(err));
        }),

    // Lightweight sample-record search for the document-template preview drawer,
    // scoped to a single CMS so another shop's submissions are never returned.
    // Filters are passed as a variable (the PaginationFilterInput "Any" scalar
    // drops inline-literal values via its null parseLiteral).
    fetchPreviewSamples: ({ cmsId = null, search = null } = {}) =>
        new Promise((resolve, reject) => {
            const filters = [{ key: 'created_by_email', like: '%@%' }];
            if (cmsId) filters.push({ key: 'cms', match: cmsId });
            if (search) filters.push({ key: 'created_by_name', like: `%${search}%` });

            apolloClient
                .query({ query: PreviewSampleFormEntriesGql, fetchPolicy: 'network-only', variables: { filters } })
                .then(({ data }) => resolve(_.get(data, 'EX_FRMS_FormEntriesPaginated.items', [])))
                .catch(err => reject(err));
        }),
    // fetchList: (cmsID = null) =>
    //     new Promise((resolve, reject) => {
    //         let variables = {};
    //         if (cmsID) variables.cms = cmsID;
    //
    //         apolloClient
    //             .query({
    //                 query: FormEntriesGql,
    //                 variables
    //             })
    //             .then(({ data }) => resolve(data.EX_FRMS_FormEntries))
    //             .catch(err => reject(err));
    //     }),
    fetchFormEntriesPaginated: data =>
        new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            if (!filters.find(f => f.key === 'created_by_email'))
                filters.push({ key: 'created_by_email', like: '%@%' });
            if (!filters.find(f => f.key === 'created_by_name'))
                filters.push({ key: 'created_by_name', like: '%' });

            apolloClient
                .query({
                    query: FormEntriesPaginatedGql,
                    variables: {
                        currentPage: _.get(data, 'pageInfo.currentPage', 1),
                        perPage: _.get(data, 'pageInfo.perPage', 25),
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                        link: _.get(data, 'link.route', '/event-manager/event/'),
                        filters
                    }
                })
                .then(({ data }) => resolve(data.EX_FRMS_FormEntriesPaginated))
                .catch(err => reject(err));
        }),
    // Build the preview URL for the notification email of a form entry.
    formEntryEmailRenderingUrl: id => {
        const token = AuthApi.getToken();
        return `${PREVIEW_ENDPOINT}form-received/${id}?token=${token}`;
    },
    // Fetch the rendered notification email ({ document, subject }) for a form entry.
    renderEmail: id =>
        new Promise((resolve, reject) => {
            ensureFreshToken()
                .then(() => axios.get(`${PREVIEW_ENDPOINT}form-received/${id}?token=${AuthApi.getToken()}`))
                .then(result => resolve(result))
                .catch(err => reject(err));
        }),
    processFormEntriesPaginationBulkAction: (action, data) => {
        return new Promise((resolve, reject) => {
            let filters = [];
            _.each(data.filters, entry => filters.push(entry));

            apolloClient
                .mutate({
                    mutation: ProcessFormEntriesPaginationBulkActionGql,
                    variables: {
                        action,
                        filters,
                        selection: {
                            type: _.get(data, 'pageInfo.selection.type', 'inclusive'),
                            identifiers: _.get(data, 'pageInfo.selection.identifiers', []),
                        },
                        order: _.get(data, 'variables.order', 'created_at'),
                        direction: _.get(data, 'variables.direction', 'desc'),
                    },
                })
                .then(({ data }) => resolve(data.EX_FRMS_ProcessFormEntriesPaginationBulkAction))
                .catch(err => reject(err));
        });
    },
    // fetchFormEntry: (id, cmsID = null) => {
    //     if (id === undefined) throw new Error('required argument is missing');
    //
    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .query({
    //                 query: FormEntryGql,
    //                 variables: { id, cms: cmsID }
    //             })
    //             .then(({ data }) => resolve(data.EX_FRMS_FormEntry))
    //             .catch(err => reject(err));
    //     });
    // },
    // create: event => {
    //     if (event === undefined) throw new Error('required argument is missing');
    //
    //     event = _.removeDeepByKey(event, '__typename');
    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .mutate({
    //                 mutation: CreateFormEntryGql,
    //                 variables: { event }
    //             })
    //             .then(({ data }) => {
    //                 apolloClient.clearStore();
    //                 resolve(data.EX_FRMS_CreateFormEntry);
    //             })
    //             .catch(err => reject(err));
    //     });
    // },
    // update: event => {
    //     if (event === undefined) throw new Error('required argument is missing');
    //
    //     event = _.removeDeepByKey(event, '__typename');
    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .mutate({
    //                 mutation: UpdateFormEntryGql,
    //                 variables: { event }
    //             })
    //             .then(({ data }) => {
    //                 apolloClient.clearStore();
    //                 resolve(data.EX_FRMS_UpdateFormEntry);
    //             })
    //             .catch(err => reject(err));
    //     });
    // },
    // delete: id => {
    //     if (id === undefined) throw new Error('required argument is missing');
    //
    //     return new Promise((resolve, reject) => {
    //         apolloClient
    //             .mutate({
    //                 mutation: DeleteFormEntryGql,
    //                 variables: { id }
    //             })
    //             .then(({ data }) => {
    //                 apolloClient.clearStore();
    //                 resolve(data.EX_FRMS_DeleteFormEntry);
    //             })
    //             .catch(err => reject(err));
    //     });
    // }
};